import { useState } from "react";

import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { PublishedMoveLine } from "~/types/contabilidad";

interface LibroDiarioTableProps {
  lines: PublishedMoveLine[];
  isLoading: boolean;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function LibroDiarioTable({ lines, isLoading }: LibroDiarioTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const totalPages = Math.ceil(lines.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedLines = lines.slice(startIndex, endIndex);

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(10)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">
          No hay comprobantes publicados en el rango seleccionado.
        </p>
        <p className="text-sm text-muted-foreground">
          Selecciona un rango de fechas diferente o verifica que existan
          comprobantes publicados.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Fecha</TableHead>
              <TableHead className="w-[120px]">N° Asiento</TableHead>
              <TableHead className="w-[100px]">Diario</TableHead>
              <TableHead className="w-[100px]">Cuenta</TableHead>
              <TableHead>Nombre Cuenta</TableHead>
              <TableHead>Descripción</TableHead>
              <TableHead className="w-[120px] text-right">Debe</TableHead>
              <TableHead className="w-[120px] text-right">Haber</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedLines.map((line, index) => (
              <TableRow
                key={line.line_id}
                className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
              >
                <TableCell className="text-muted-foreground">
                  {dayjs(line.move_date).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {line.move_number}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {line.journal_name}
                </TableCell>
                <TableCell className="font-mono text-sm font-medium">
                  {line.account_code}
                </TableCell>
                <TableCell>{line.account_name}</TableCell>
                <TableCell className="text-muted-foreground">
                  {line.description || "-"}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {Number(line.debit) > 0
                    ? formatCurrency(Number(line.debit))
                    : "-"}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {Number(line.credit) > 0
                    ? formatCurrency(Number(line.credit))
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Mostrar</span>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>por página</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {startIndex + 1}-{Math.min(endIndex, lines.length)} de{" "}
            {lines.length}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
